/**
 * Notification Service
 * 
 * Centralized service for displaying notifications across the app.
 * Provides a unified API for success, error, warning, and info messages.
 */

import { toast } from "sonner";
import { i18n } from '@/core';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationOptions {
  /** Duration in milliseconds. Default: 4000 */
  duration?: number;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Optional description text */
  description?: string;
  /** Whether the notification can be dismissed */
  dismissible?: boolean;
}

function resolveI18nText(text?: string): string | undefined {
  if (!text) return text;
  return i18n.exists(text) ? i18n.t(text) : text;
}

/**
 * Display a success notification
 */
export function notifySuccess(message: string, options?: NotificationOptions) {
  return toast.success(resolveI18nText(message) ?? message, {
    duration: options?.duration ?? 4000,
    description: resolveI18nText(options?.description),
    dismissible: options?.dismissible ?? true,
    action: options?.action ? {
      label: resolveI18nText(options.action.label) ?? options.action.label,
      onClick: options.action.onClick,
    } : undefined,
  });
}

/**
 * Display an error notification
 */
export function notifyError(message: string, options?: NotificationOptions) {
  return toast.error(resolveI18nText(message) ?? message, {
    duration: options?.duration ?? 6000,
    description: resolveI18nText(options?.description),
    dismissible: options?.dismissible ?? true,
    action: options?.action ? {
      label: resolveI18nText(options.action.label) ?? options.action.label,
      onClick: options.action.onClick,
    } : undefined,
  });
}

/**
 * Display a warning notification
 */
export function notifyWarning(message: string, options?: NotificationOptions) {
  return toast.warning(resolveI18nText(message) ?? message, {
    duration: options?.duration ?? 5000,
    description: resolveI18nText(options?.description),
    dismissible: options?.dismissible ?? true,
    action: options?.action ? {
      label: resolveI18nText(options.action.label) ?? options.action.label,
      onClick: options.action.onClick,
    } : undefined,
  });
}

/**
 * Display an info notification
 */
export function notifyInfo(message: string, options?: NotificationOptions) {
  return toast.info(resolveI18nText(message) ?? message, {
    duration: options?.duration ?? 4000,
    description: resolveI18nText(options?.description),
    dismissible: options?.dismissible ?? true,
    action: options?.action ? {
      label: resolveI18nText(options.action.label) ?? options.action.label,
      onClick: options.action.onClick,
    } : undefined,
  });
}

/**
 * Display a loading notification that can be updated
 */
export function notifyLoading(message: string) {
  return toast.loading(resolveI18nText(message) ?? message);
}

/**
 * Dismiss a specific notification by ID
 */
export function dismissNotification(id: string | number) {
  toast.dismiss(id);
}

/**
 * Dismiss all notifications
 */
export function dismissAllNotifications() {
  toast.dismiss();
}

/**
 * Promise-based notification for async operations
 */
export function notifyPromise<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: unknown) => string);
  }
) {
  return toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: messages.error,
  });
}

/**
 * Unified notification object for easy importing
 */
export const notify = {
  success: notifySuccess,
  error: notifyError,
  warning: notifyWarning,
  info: notifyInfo,
  loading: notifyLoading,
  dismiss: dismissNotification,
  dismissAll: dismissAllNotifications,
  promise: notifyPromise,
};

export default notify;
